/**
 * Shared HTML sanitization helpers.
 *
 * The codebase has a few places that need to either:
 *   a) inject HTML built from possibly-untrusted strings (resume content,
 *      blog body, AI streaming output), OR
 *   b) parse an HTML fragment back to text/markdown via a temporary off-DOM
 *      element.
 *
 * Both cases must go through DOMPurify so a malicious payload like
 * `<img src=x onerror=alert(1)>` or `<script>...</script>` can never execute
 * — even if the off-DOM element is later attached to the document (e.g. by
 * html2canvas during PDF export) or if the calling code forgets to escape.
 *
 * Prefer `safeHtml()` for `dangerouslySetInnerHTML` and `setInnerHtml()` for
 * imperative DOM writes. NEVER call `el.innerHTML = userString` directly.
 */
import DOMPurify from 'dompurify';

/**
 * Conservative allowlist suitable for rich-text-like content (resume bodies,
 * blog posts, in-app previews). Allows common semantic tags + links + lists +
 * tables, but strips every script vector (script/style/iframe/object/embed,
 * inline event handlers, javascript: URLs).
 */
const RICH_TEXT_CONFIG: DOMPurify.Config = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: [
    'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseenter', 'onmouseleave',
    'onfocus', 'onblur', 'onchange', 'oninput', 'onsubmit', 'onkeydown', 'onkeyup',
    'onanimationstart', 'onanimationend', 'ontransitionend', 'onpointerdown',
  ],
};

/**
 * Minimal allowlist for inline formatting only — useful for previews that
 * should never render block elements (e.g. inline AI streaming output).
 */
const INLINE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['strong', 'b', 'em', 'i', 'u', 'br', 'span', 'mark', 'code'],
  ALLOWED_ATTR: ['class'],
};

/**
 * Sanitize an HTML string for safe use with `dangerouslySetInnerHTML`.
 *
 * @example
 *   <div dangerouslySetInnerHTML={{ __html: safeHtml(userBody) }} />
 */
export function safeHtml(input: string | null | undefined): string {
  if (input == null) return '';
  return DOMPurify.sanitize(String(input), RICH_TEXT_CONFIG) as string;
}

/** Like `safeHtml` but only permits inline tags (no headings, lists, etc.) */
export function safeInlineHtml(input: string | null | undefined): string {
  if (input == null) return '';
  return DOMPurify.sanitize(String(input), INLINE_CONFIG) as string;
}

/**
 * Imperative replacement for `el.innerHTML = html`. Always go through this
 * helper — never assign `innerHTML` directly when the right-hand side could
 * be derived from user input.
 */
export function setInnerHtml(el: HTMLElement, html: string | null | undefined): void {
  el.innerHTML = safeHtml(html);
}

/**
 * Escape a string for safe injection into HTML text content. Use this when
 * you control the surrounding markup and just need to escape one piece of
 * text (preferred over DOMPurify for pure-text cases — cheaper and clearer).
 */
export function escapeHtml(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
